/////////////////////////////////////////////////////////
// Configurations related to user.                     //
/////////////////////////////////////////////////////////

// Note: The userFields come from userFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for user fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public', 'protected', or 'private'.
 *                                  Default value: 'public'.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs on
 *                                  ProfileSettingsPage and AuthenticationPage.
 * - showConfig:                    Configuration for rendering user information. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - displayInProfile (optional):   Can be used to hide field content from profile page.
 *                                    Default value: true.
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for users to fill
 *   - requiredMessage (optional):    Message for mandatory fields.
 *   - displayInSignUp (optional):    Can be used to show field input on sign up page.
 *                                    Default value: true.
 * - userTypeConfig:                Configuration for limiting user field to specific user types.
 *   - limitToUserTypeIds:            Can be used to determine whether to limit the field to certain user types. The
 *                                    Console based asset configurations do not yet support user types, so in hosted configurations
 *                                    the default value for this is 'false'.
 *   - userTypeIds:                   An array of user types for which the extended
 *   (optional)                       data is relevant and should be added.
 */
export const userFields = [
  // ─── AGE VERIFICATION (Push 10) ─────────────────────────────────────
  // Date of birth, collected at signup for both customer and provider
  // user types. Stored as ISO `YYYY-MM-DD` text in protectedData. Used by:
  //   - Signup gate (under-18 blocks account creation; defense-in-depth
  //     at both CustomFieldDate validation and signupThunk validation)
  //   - Stripe Identity webhook (overwrites with verified DOB at
  //     verification time when over 18; suspends account when under)
  //   - Booking-initiation age gate (server-side, compares against
  //     listing.publicData.minimum_renter_age)
  //   - Waiver/agreement `customer.dob` merge chip (auto-merge)
  // Scope `protected` because DOB is PII — providers don't see customer
  // DOBs; only the platform / operator does. The schemaType `date` is a
  // client-side rendering hint (Sharetribe-side schema is text); see
  // SCHEMA_TYPE_DATE in src/util/types.js for the dispatcher mapping.
  {
    key: 'dateOfBirth',
    scope: 'protected',
    schemaType: 'date',
    saveConfig: {
      label: 'Date of birth',
      displayInSignUp: true,
      isRequired: true,
    },
    // dateOfBirth applies to ALL user types (customer + provider). The
    // codebase convention is for every userField to declare a
    // userTypeConfig block; `limitToUserTypeIds: false` is the "no
    // restriction" sentinel consumed at userHelpers.js:47 via the
    // `!userTypeConfig.limitToUserTypeIds` short-circuit. Without this
    // block, pickUserFieldsData crashes on `userTypeConfig.limitToUserTypeIds`
    // (no guard in the helper).
    userTypeConfig: {
      limitToUserTypeIds: false,
    },
  },
  // ─── SALES TAX COMPLIANCE ───────────────────────────────────────────
  {
    key: 'providerTaxStatus',
    scope: 'private',
    schemaType: 'enum',
    enumOptions: [
      { option: 'exempt', label: 'Exempt — I am not required to collect sales tax' },
      { option: 'taxable', label: 'Taxable — I collect and remit sales tax on rentals' },
    ],
    saveConfig: {
      label: 'Are your rentals subject to sales tax?',
      displayInSignUp: true,
      isRequired: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['provider'],
    },
  },
  {
    key: 'taxJurisdictionState',
    scope: 'private',
    schemaType: 'enum',
    enumOptions: [
      { option: 'AL', label: 'Alabama' },
      { option: 'AK', label: 'Alaska' },
      { option: 'AZ', label: 'Arizona' },
      { option: 'AR', label: 'Arkansas' },
      { option: 'CA', label: 'California' },
      { option: 'CO', label: 'Colorado' },
      { option: 'CT', label: 'Connecticut' },
      { option: 'DE', label: 'Delaware' },
      { option: 'FL', label: 'Florida' },
      { option: 'GA', label: 'Georgia' },
      { option: 'HI', label: 'Hawaii' },
      { option: 'ID', label: 'Idaho' },
      { option: 'IL', label: 'Illinois' },
      { option: 'IN', label: 'Indiana' },
      { option: 'IA', label: 'Iowa' },
      { option: 'KS', label: 'Kansas' },
      { option: 'KY', label: 'Kentucky' },
      { option: 'LA', label: 'Louisiana' },
      { option: 'ME', label: 'Maine' },
      { option: 'MD', label: 'Maryland' },
      { option: 'MA', label: 'Massachusetts' },
      { option: 'MI', label: 'Michigan' },
      { option: 'MN', label: 'Minnesota' },
      { option: 'MS', label: 'Mississippi' },
      { option: 'MO', label: 'Missouri' },
      { option: 'MT', label: 'Montana' },
      { option: 'NE', label: 'Nebraska' },
      { option: 'NV', label: 'Nevada' },
      { option: 'NH', label: 'New Hampshire' },
      { option: 'NJ', label: 'New Jersey' },
      { option: 'NM', label: 'New Mexico' },
      { option: 'NY', label: 'New York' },
      { option: 'NC', label: 'North Carolina' },
      { option: 'ND', label: 'North Dakota' },
      { option: 'OH', label: 'Ohio' },
      { option: 'OK', label: 'Oklahoma' },
      { option: 'OR', label: 'Oregon' },
      { option: 'PA', label: 'Pennsylvania' },
      { option: 'RI', label: 'Rhode Island' },
      { option: 'SC', label: 'South Carolina' },
      { option: 'SD', label: 'South Dakota' },
      { option: 'TN', label: 'Tennessee' },
      { option: 'TX', label: 'Texas' },
      { option: 'UT', label: 'Utah' },
      { option: 'VT', label: 'Vermont' },
      { option: 'VA', label: 'Virginia' },
      { option: 'WA', label: 'Washington' },
      { option: 'WV', label: 'West Virginia' },
      { option: 'WI', label: 'Wisconsin' },
      { option: 'WY', label: 'Wyoming' },
    ],
    saveConfig: {
      label: 'Tax jurisdiction (only required if you selected Taxable above)',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['provider'],
    },
  },
  {
    key: 'sellerPermitNumber',
    scope: 'private',
    schemaType: 'text',
    saveConfig: {
      label: "Seller's Permit Number (only required if you selected Taxable above)",
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['provider'],
    },
  },
  {
    key: 'taxAcknowledgment',
    scope: 'private',
    schemaType: 'boolean',
    saveConfig: {
      label:
        'I understand that I am solely responsible for determining, collecting, and remitting any applicable sales tax on my rental income. BoatersNetwork displays tax amounts as a convenience but does not collect, hold, or remit sales tax on my behalf.',
      displayInSignUp: true,
      isRequired: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['provider'],
    },
  },
];

/////////////////////////////////////
// Example user type configuration //
/////////////////////////////////////
/**
 * User types are not supported in hosted configuration yet.
 *
 * To take user types into use in your
 * custom code, you can do the following things:
 * - Add a new user field with key 'userType', scope 'publicData', and schemaType enum
 *  - Consider whether or not you want to allow your users to change their user type after first creating it
 * - Set your user types as the available options for the userType field
 * - Add your user types in the array below
 * - Update configHelpers.js mergeUserConfig to pass user types to the validUserFields function
 */

export const userTypes = [
  {
    userType: 'customer',
    label: 'Customer',
  },
  {
    userType: 'provider',
    label: 'Provider',
  },
];
